import { useState, useRef } from "react";
import Button from "./Button";
import DatePicker from "./DatePicker";
import DateRangePicker from "./DateRangePicker";
import SearchInput from "./SearchInput";

const SearchAndFilterBar = ({
  showSearch = true,
  showAddButton = true,
  searchPlaceholder = "Tìm kiếm",
  searchValue,
  onSearchChange,
  onSearchClick,
  showDatePickers = false,
  fromDatePlaceholder = "Từ ngày",
  toDatePlaceholder = "Đến ngày",
  onFromDateChange,
  onToDateChange,
  onAddClick,
  addButtonLabel = "Thêm",
  addButtonIcon,
  filterButton = true,
  filterButtonVariant = "secondary",
  filterLabel = "Bộ lọc",
  widthButton,
  showDateRangePickers = false,
  fromDateValue,
  toDateValue,
  onFilterClick,
}) => {
  const [openPicker, setOpenPicker] = useState(null);

  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const dateRangeButtonRef = useRef(null);
  const [internalFromDate, setInternalFromDate] = useState(null);
  const [internalToDate, setInternalToDate] = useState(null);

  const handleDatePickerToggle = (picker) => {
    setOpenPicker((prev) => (prev === picker ? null : picker));
  };

  const isControlled = fromDateValue !== undefined || toDateValue !== undefined;

  const parseDateStr = (str) => {
    if (!str) return null;
    const [dd, mm, yyyy] = str.split("-");
    if (!dd || !mm || !yyyy) return null;
    return new Date(yyyy, parseInt(mm) - 1, parseInt(dd));
  };

  const formatDateToStr = (date) => {
    if (!date) return "";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const currentFromDate = isControlled
    ? parseDateStr(fromDateValue)
    : internalFromDate;

  const currentToDate = isControlled
    ? parseDateStr(toDateValue)
    : internalToDate;

  const handleDateRangeApply = ({ startDate, endDate }) => {
    const fromDateStr = formatDateToStr(startDate);
    const toDateStr = formatDateToStr(endDate);

    if (!isControlled) {
      setInternalFromDate(startDate);
      setInternalToDate(endDate);
    }

    if (onFromDateChange) {
      onFromDateChange(fromDateStr, startDate);
    }
    if (onToDateChange) {
      onToDateChange(toDateStr, endDate);
    }

    setIsDateRangePickerOpen(false);
  };

  const handleDateRangeCancel = () => {
    setIsDateRangePickerOpen(false);
  };

  const getDateRangeDisplayText = () => {
    if (currentFromDate && currentToDate) {
      return `${formatDateToStr(currentFromDate)} - ${formatDateToStr(currentToDate)}`;
    }
    if (currentFromDate) {
      return `${formatDateToStr(currentFromDate)} - ...`;
    }
    return `${fromDatePlaceholder} - ${toDatePlaceholder}`;
  };

  const handleClearDates = (e) => {
    e.stopPropagation();

    if (!isControlled) {
      setInternalFromDate(null);
      setInternalToDate(null);
    }

    if (onFromDateChange) {
      onFromDateChange("", null);
    }
    if (onToDateChange) {
      onToDateChange("", null);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-end gap-4 w-full">
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 w-full lg:w-auto">
        {showSearch && (
          <SearchInput
            value={searchValue}
            placeholder={searchPlaceholder}
            onChange={onSearchChange}
            onSearch={onSearchClick}
            className="sm:w-[320px] lg:w-[280px] xl:w-[320px]"
          />
        )}

        {showDatePickers && (
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="w-full sm:w-[150px] md:w-[180px] lg:w-[200px]">
              <DatePicker
                placeholder={fromDatePlaceholder}
                onChange={onFromDateChange}
                height="42px"
                isCalendarOpen={openPicker === "fromDate"}
                onToggle={() => handleDatePickerToggle("fromDate")}
              />
            </div>

            <div className="w-full sm:w-[150px] md:w-[180px] lg:w-[200px]">
              <DatePicker
                placeholder={toDatePlaceholder}
                onChange={onToDateChange}
                height="42px"
                isCalendarOpen={openPicker === "toDate"}
                onToggle={() => handleDatePickerToggle("toDate")}
              />
            </div>
          </div>
        )}

        {showDateRangePickers && (
          <div className="relative w-full sm:w-auto">
            <button
              ref={dateRangeButtonRef}
              onClick={() => setIsDateRangePickerOpen(!isDateRangePickerOpen)}
              className={`
                flex items-center justify-between gap-3 
                border rounded-lg px-4 py-2.5 
                bg-white transition-all
                w-full sm:min-w-[240px] h-[42px]
                ${
                  isDateRangePickerOpen
                    ? "border-[#0457C2] ring-2 ring-[#0457C2]/20"
                    : "border-gray-200 hover:border-gray-400"
                }
              `}
            >
              <div className="flex items-center gap-2 overflow-hidden">
                <svg
                  className="w-5 h-5 text-gray-400 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span
                  className={`text-sm truncate ${
                    currentFromDate && currentToDate
                      ? "text-gray-900"
                      : "text-gray-400"
                  }`}
                >
                  {getDateRangeDisplayText()}
                </span>
              </div>

              {currentFromDate && currentToDate && (
                <button
                  onClick={handleClearDates}
                  className="p-1 hover:bg-gray-100 rounded-full transition-colors shrink-0"
                >
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </button>

            {isDateRangePickerOpen && (
              <DateRangePicker
                anchorRef={dateRangeButtonRef}
                onApply={handleDateRangeApply}
                onCancel={handleDateRangeCancel}
                initialStartDate={currentFromDate}
                initialEndDate={currentToDate}
                align="left"
              />
            )}
          </div>
        )}

        {filterButton && (
          <Button
            type="button"
            variant={filterButtonVariant}
            size="lg"
            onClick={onFilterClick}
            className="w-full sm:w-auto"
            aria-label={filterLabel}
          >
            {filterLabel}
          </Button>
        )}
      </div>

      {showAddButton && (
        <Button
          variant="primary"
          size="lg"
          onClick={onAddClick}
          style={{ width: widthButton }}
          aria-label={addButtonLabel}
          leftIcon={
            addButtonIcon ? (
              <img src={addButtonIcon} alt="" className="size-4" />
            ) : null
          }
        >
          {addButtonLabel}
        </Button>
      )}
    </div>
  );
};

export default SearchAndFilterBar;
